import { isColocaDebit } from "@/modules/dashboard/wallet-debits/utils/walletDebit";
import { isColocaTopUp } from "@/modules/dashboard/wallet-top-ups/utils/walletTopUp";
import { formatRail } from "@/shared/utils/railDisplay";

import type { LedgerOperationType, RuntimeWalletTransaction } from "../types/payments-wallets.types";
import type { RecentTransaction } from "../types/wallets-api.types";

/**
 * Adapts a runtime ledger leg into the compact `RecentTransaction` the wallet cards render. Sign convention
 * (handoff §3.5): `amount` is always positive; CREDIT (`C`) is money in (positive), DEBIT (`D`) is money out
 * (negative). Ledger legs are already posted, so there is no in-flight "processing" status.
 */
export const mapRuntimeTransaction = (
  tx: RuntimeWalletTransaction,
  operationLabels: Record<LedgerOperationType, string>,
  /**
   * Translated subtitle for the leg. Optional so the mapper keeps working without it, but every caller should
   * pass `useWalletEntryLabels().describe` — the raw `description` is English free text written by the
   * runtime ("payout reserve"), which is exactly what a client should never read.
   */
  describe?: (tx: RuntimeWalletTransaction) => string | null,
  /** Translated status of the owning operation, appended so a settled payout stops reading "reserve". */
  statusOf?: (tx: RuntimeWalletTransaction) => string | null,
): RecentTransaction => {
  const magnitude = Number(tx.amount);
  const signed = tx.direction === "C" ? magnitude : -magnitude;
  // A Coloca top-up arrives from outside the organization, like a collection, and its description is its title;
  // a Coloca debit for a manual send leaves for outside it, and reads the same way.
  const colocaMovement = isColocaTopUp(tx) || isColocaDebit(tx);
  // A card loaded from this wallet is named by its card, and the network that carried it says nothing.
  const cardTopUp = !!tx.operation_card_top_up;
  const isExternal = colocaMovement || tx.operation_type === "PAYIN" || tx.operation_type === "PAYOUT";
  const title = operationLabels[tx.operation_type] ?? tx.operation_type;
  return {
    id: tx.id,
    title: colocaMovement || cardTopUp ? (describe?.(tx) ?? title) : title,
    ts: tx.posted_at,
    amount: signed,
    currency: tx.currency,
    kind: isExternal ? "external" : "internal",
    subtitle: colocaMovement
      ? null
      : cardTopUp
        ? (statusOf?.(tx) ?? null)
        : buildSubtitle(tx, describe, statusOf),
    method: tx.operation_rail && !cardTopUp ? formatRail(tx.operation_rail) : null,
    raw: tx,
  };
};

/**
 * "Salida de dispersión · Pagada". The status matters because a leg is immutable while the operation is not:
 * the wallet only ever sees a payout's RESERVE entry, so without it the row reads "reserve" for a payout that
 * settled days ago.
 */
function buildSubtitle(
  tx: RuntimeWalletTransaction,
  describe?: (tx: RuntimeWalletTransaction) => string | null,
  statusOf?: (tx: RuntimeWalletTransaction) => string | null,
): string | null {
  const label = describe ? describe(tx) : (tx.description ?? null);
  const status = statusOf?.(tx) ?? null;
  if (label && status) return `${label} · ${status}`;
  return label ?? status;
}
